// Diagnosis-delta preview for the pathway picker: what the recorded diagnosis
// becomes if this subtrack is applied. Pure resolver math — Apply is the
// clinician's attestation of the delta (spec 2026-07-17-care-plan-conditions-subtrack-coherence).
import {
  reconcileConditionsForSubtrack,
  deriveIcdBundle,
  normalizeCondition,
  type SubtrackConditionReconcile,
} from '../../../scripts/access-subtrack-resolver.mjs';

export type DiagnosisDelta = SubtrackConditionReconcile;

export function diagnosisDeltaForSubtrack(
  currentConditions: unknown,
  subtrackId: string | null | undefined,
): DiagnosisDelta | null {
  if (!subtrackId) return null;
  // Present-but-malformed conditions → no delta (never propose replacing a diagnosis we can't read).
  if (currentConditions != null && !Array.isArray(currentConditions)) return null;
  const arr = Array.isArray(currentConditions) ? currentConditions.map(String) : [];
  return reconcileConditionsForSubtrack(arr, String(subtrackId));
}

export function hasDiagnosisChange(delta: DiagnosisDelta | null): boolean {
  return !!delta && (delta.added.length > 0 || delta.removed.length > 0);
}

// Non-vocabulary removals (extended/legacy free text) get an explanatory label.
export function isVocabularyCondition(value: string): boolean {
  return normalizeCondition(value) != null;
}

// ── Panel rows: one chip per condition, billed ICD-10 inline ──
// Same math as the alignment packet (deriveIcdBundle honoring the subtrack), so
// the codes shown ARE what $align will submit — incl. combination codes
// (DM2+CKD → E11.22 on the T2D chip + N18.30 on the CKD chip; the chip-code
// union equals the billed set) and the subtrack-only rule (comorbidities carry
// "chart only" instead of a code — attested + written, never billed).

export interface PanelConditionRow {
  label: string;
  state: 'added' | 'kept' | 'removed';
  code: string | null; // billed ICD-10 (removed rows: the code leaving the CMS set)
  display: string | null;
  chartOnly: boolean;
  unsupported: boolean; // non-vocabulary removal (extended/legacy free text)
}

// Billed code for one condition token within a cms_icd10 set: the exact
// single-token row wins (CKD → N18.30), else the combination row containing it
// (T2D → E11.22). Null when the token isn't billed (chart-only comorbidity).
function billedCodeFor(token: string, cmsRows: Array<{ icd10: string; display: string; conditions: string[] }>) {
  const exact = cmsRows.find((r) => r.conditions.length === 1 && r.conditions[0] === token);
  const row = exact ?? cmsRows.find((r) => r.conditions.includes(token));
  return row ? { code: row.icd10, display: row.display } : null;
}

export function panelConditionRows(
  currentConditions: unknown,
  currentSubtrackId: string | null | undefined,
  delta: DiagnosisDelta | null,
  newSubtrackId: string | null | undefined,
): PanelConditionRow[] {
  if (!delta || !newSubtrackId) return [];
  const cur = Array.isArray(currentConditions) ? currentConditions.map(String) : [];
  const after = deriveIcdBundle({ conditions: delta.proposed, subtrack_id: String(newSubtrackId) }).cms_icd10;
  // "Before" = what an alignment fired TODAY would submit (stored subtrack honored) —
  // gives a removed condition the code that stops being billed.
  const before = deriveIcdBundle({ conditions: cur, subtrack_id: currentSubtrackId ?? null }).cms_icd10;

  const rows: PanelConditionRow[] = delta.proposed.map((label) => {
    const tok = normalizeCondition(label);
    const billed = tok ? billedCodeFor(tok, after) : null;
    return {
      label,
      state: delta.added.includes(label) ? 'added' : 'kept',
      code: billed?.code ?? null,
      display: billed?.display ?? null,
      chartOnly: billed == null,
      unsupported: false,
    };
  });
  delta.removed.forEach((label) => {
    const tok = normalizeCondition(label);
    const wasBilled = tok ? billedCodeFor(tok, before) : null;
    rows.push({
      label,
      state: 'removed',
      code: wasBilled?.code ?? null,
      display: wasBilled?.display ?? null,
      chartOnly: false,
      unsupported: tok == null,
    });
  });
  return rows;
}
